import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const NAV = [
    { to: "/maison", label: t.nav.maison, key: "maison" },
    { to: "/collection", label: t.nav.collection, key: "collection" },
    { to: "/univers#etudes-privees", label: t.nav.univers, key: "etudes" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const langPair = (size = "desktop") => (
    <div
      className={`flex items-center gap-2 ${size === "mobile" ? "text-[1rem]" : ""}`}
      data-testid="lang-toggle"
    >
      <button
        type="button"
        onClick={() => setLang("fr")}
        data-testid="lang-toggle-fr"
        aria-pressed={lang === "fr"}
        className={`font-eyebrow transition-colors duration-300 ${
          lang === "fr"
            ? "text-[hsl(var(--md-ink))]"
            : "text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))]"
        }`}
      >
        FR
      </button>
      <span className="text-[hsl(var(--md-stone))]" aria-hidden="true">·</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        data-testid="lang-toggle-en"
        aria-pressed={lang === "en"}
        className={`font-eyebrow transition-colors duration-300 ${
          lang === "en"
            ? "text-[hsl(var(--md-ink))]"
            : "text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))]"
        }`}
      >
        EN
      </button>
    </div>
  );

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(var(--md-chalk))]/92 backdrop-blur-md border-b border-[hsl(var(--md-line))]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 h-[72px] md:h-[88px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="font-display text-[1.35rem] md:text-[1.55rem] tracking-[-0.01em] text-[hsl(var(--md-ink))] leading-none"
        >
          Maison&nbsp;Dodji
        </Link>

        <nav className="hidden lg:flex items-center gap-12">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `font-eyebrow text-[11px] transition-colors duration-300 ${
                  isActive
                    ? "text-[hsl(var(--md-ink))]"
                    : "text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <span className="w-px h-3 bg-[hsl(var(--md-line))]" aria-hidden="true" />
          {langPair()}
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[hsl(var(--md-ink))] p-2 -mr-2"
        >
          {open ? <X size={20} strokeWidth={1.25} /> : <Menu size={20} strokeWidth={1.25} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0"
        } bg-[hsl(var(--md-bone))] border-t border-[hsl(var(--md-line))]`}
        data-testid="mobile-menu"
      >
        <div className="px-8 py-10 flex flex-col gap-6">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className="font-eyebrow text-[15px] tracking-[0.22em] text-[hsl(var(--md-ink))]"
              data-testid={`mobile-nav-${item.key}`}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="hairline mt-2" />
          <div className="flex items-center justify-between">
            <p className="font-body text-[13px] tracking-[0.02em] text-[hsl(var(--md-stone))]">contact@maisondodji.com</p>
            {langPair("mobile")}
          </div>
        </div>
      </div>
    </header>
  );
};
